import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";

const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const InvoiceUploader = ({ value, onChange, error, helperText, disabled = false }) => {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const [sizeError, setSizeError] = useState(null);

  const validateAndSetFile = (file) => {
    setSizeError(null);
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Only images (JPEG, PNG, WebP) and PDFs are allowed.");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setSizeError(`File size exceeds ${MAX_SIZE_MB}MB limit. Please choose a smaller file.`);
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result;
      setPreview(base64Data);
      onChange(base64Data, file.name);
    };
    reader.onerror = () => {
      alert("Error reading file. Please try again.");
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreview(null);
    onChange(null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    setCameraError(null);
    // Blur so focus is not on this button when nested dialog opens (avoids aria-hidden violation)
    if (typeof document !== "undefined") {
      document.activeElement?.blur?.();
    }
    setCameraOpen(true);
  };

  // Start camera stream after capture dialog is open and previous stream has been released
  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;
    let timeoutId;

    const startStream = () => {
      const video = videoRef.current;
      if (!video) return;
      (async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" },
            audio: false,
          });
          if (cancelled || !videoRef.current) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          videoRef.current.srcObject = stream;
        } catch (err) {
          if (!cancelled) {
            setCameraError(
              err.name === "NotAllowedError"
                ? "Camera access was denied."
                : "Could not access camera. Try uploading a file instead."
            );
          }
        }
      })();
    };

    // Delay so: (1) capture dialog and <video> are mounted, (2) parent modal focus/aria is settled, (3) previous camera is released
    timeoutId = setTimeout(startStream, 350);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [cameraOpen]);

  const handleCapturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setCameraOpen(false);
        validateAndSetFile(file);
      },
      "image/jpeg",
      0.9
    );
  };

  const handleCloseCamera = () => {
    setCameraOpen(false);
    setCameraError(null);
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      <Dialog
        open={cameraOpen}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle id="capture-invoice-dialog-title">Capture invoice image</DialogTitle>
        <DialogContent>
          {cameraError ? (
            <Alert severity="error">{cameraError}</Alert>
          ) : (
            <Box
              sx={{
                width: "100%",
                borderRadius: 1,
                overflow: "hidden",
                background: "#000",
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCamera}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PhotoCameraIcon />}
            onClick={handleCapturePhoto}
            disabled={!!cameraError}
          >
            Take photo
          </Button>
        </DialogActions>
      </Dialog>

      {!preview && !value ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: "center",
            border: error ? "2px dashed" : "1px dashed",
            borderColor: error ? "error.main" : "divider",
            backgroundColor: disabled ? "action.disabledBackground" : "background.default",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.7 : 1,
            "&:hover": disabled ? {} : { backgroundColor: "action.hover" },
          }}
        >
          <Box sx={{ mb: 2 }}>
            <ImageIcon sx={{ fontSize: 48, color: "text.secondary" }} />
          </Box>
          <Typography variant="body1" gutterBottom>
            Upload Invoice or Capture Image
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Supported formats: JPEG, PNG, WebP, PDF (Max {MAX_SIZE_MB}MB)
          </Typography>
          {sizeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sizeError}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              disabled={disabled}
            >
              Upload File
            </Button>
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              onClick={handleCameraClick}
              disabled={disabled}
            >
              Capture Image
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              position: "relative",
              border: error ? "2px solid" : "1px solid",
              borderColor: error ? "error.main" : "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle2">Invoice Preview</Typography>
              <IconButton size="small" onClick={handleRemove} color="error">
                <CloseIcon />
              </IconButton>
            </Box>
            {preview || value ? (
              <Box
                component="img"
                src={preview || value}
                alt="Invoice preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  objectFit: "contain",
                  borderRadius: 1,
                }}
                onError={(e) => {
                  // If it's a PDF, show PDF icon instead
                  if ((preview || value)?.includes("application/pdf")) {
                    e.target.style.display = "none";
                  }
                }}
              />
            ) : null}
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleUploadClick}
                disabled={disabled}
              >
                Change File
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={handleCameraClick}
                disabled={disabled}
              >
                Capture New
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {error && helperText && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {helperText}
        </Alert>
      )}
      {!error && helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default InvoiceUploader;
